import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Activity,
  Building2,
  Cloud,
  Cpu,
  Globe,
  Layers,
  MapPinned,
  Menu,
  PlayCircle,
  RadioTower,
  Server,
  ShieldCheck,
  Smartphone,
  Tv,
  Zap,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Phone,
  Mail,
  MessageCircle,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";

import { siteConfig } from "@/config/siteConfig";
import { translations, getDirection } from "@/i18n";
import { LANDING } from "@/constants/testIds";
import { fetchCoverageLocations } from "@/services/coverageService";
import { coverageUtils } from "@/utils/coverageUtils";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const twinLogo = "/assets/logo/twin-tc-logo-transparent.png";
const API = "/api";

const navTargets = [
  { key: "home", href: "#home", testId: LANDING.navHome },
  { key: "internet", href: "#services", testId: LANDING.navInternet },
  { key: "coverage", href: "/coverage-map", testId: LANDING.navCoverageMap },
  { key: "twinTv", href: "#twin-tv", testId: LANDING.navTwinTv },
  { key: "noc", href: "#smart-noc", testId: LANDING.navNoc },
  { key: "cloud", href: "#cloud", testId: LANDING.navCloud },
  { key: "support", href: "#support", testId: LANDING.navSupport },
  { key: "contact", href: "#support", testId: LANDING.navContact },
];

const serviceIcons = {
  wireless: RadioTower,
  enterprise: Building2,
  coverage: MapPinned,
};

const cloudIcons = [Cloud, Server, Building2, Globe, Cpu, ShieldCheck, Layers];

const socialPlatforms = [
  { key: "facebook", icon: Facebook },
  { key: "instagram", icon: Instagram },
  { key: "x", icon: Activity },
  { key: "linkedin", icon: Linkedin },
  { key: "youtube", icon: Youtube },
];

const parsePercent = (value) => {
  const num = Number.parseFloat(String(value).replace("%", ""));
  return Number.isNaN(num) ? 0 : Math.max(0, Math.min(100, num));
};

const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-10 flex flex-col gap-3">
    <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{title}</h2>
    {subtitle ? <p className="max-w-3xl text-sm leading-8 text-muted-foreground sm:text-base">{subtitle}</p> : null}
  </div>
);

const NetworkBackdrop = () => {
  const particles = useMemo(
    () =>
      Array.from({ length: 14 }).map((_, i) => ({
        id: i,
        left: `${8 + ((i * 7) % 82)}%`,
        bottom: `${2 + ((i * 9) % 30)}%`,
        delay: `${(i % 6) * 1.2}s`,
        duration: `${9 + (i % 6)}s`,
      })),
    [],
  );

  return (
    <>
      <div className="neon-map-layer" aria-hidden="true" />
      <div className="network-grid" aria-hidden="true" />
      <svg className="world-lines" viewBox="0 0 1000 420" preserveAspectRatio="none" aria-hidden="true">
        <path d="M20 210 C 180 120, 320 120, 500 210 S 820 300, 980 210" />
        <path d="M40 300 C 210 220, 360 240, 520 290 S 820 360, 960 280" />
        <path d="M65 150 C 250 80, 390 90, 560 160 S 840 250, 960 180" />
        <path className="pulse" d="M20 210 C 180 120, 320 120, 500 210 S 820 300, 980 210" />
      </svg>
      <div className="particles" aria-hidden="true">
        {particles.map((particle) => (
          <span
            key={particle.id}
            style={{
              left: particle.left,
              bottom: particle.bottom,
              "--delay": particle.delay,
              "--duration": particle.duration,
            }}
          />
        ))}
      </div>
    </>
  );
};

const StatusRing = ({ label, value, testId }) => {
  const percentage = parsePercent(value);
  const ringStyle = {
    background: `conic-gradient(#2ef2a5 ${percentage * 3.6}deg, rgba(255,255,255,0.14) 0deg)`,
  };

  return (
    <Card className="glass-card rounded-2xl border-white/15" data-testid={testId}>
      <CardContent className="flex items-center justify-between gap-4 p-5">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 font-mono-tech text-2xl font-semibold text-foreground" data-testid={LANDING.networkStatusValue}>
            {value}
          </p>
        </div>
        <div className="metric-ring" style={ringStyle}>
          <div className="font-mono-tech text-sm font-semibold text-[#2ef2a5]">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const DeviceMockups = ({ dictionary }) => {
  return (
    <div className="relative grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
      <div className="glass-card relative rounded-2xl border-white/15 p-3 shadow-[0_0_50px_rgba(255,59,212,0.16)]">
        <div className="rounded-xl border border-white/10 bg-[#0a1122] p-4">
          <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <Tv className="h-3.5 w-3.5 text-primary" />
              {dictionary.twinTv.deviceLabel.title}
            </span>
            <span className="font-mono-tech">{dictionary.twinTv.deviceLabel.live}</span>
          </div>
          <div className="grid h-44 grid-cols-3 gap-2 rounded-lg bg-[linear-gradient(120deg,#111935,#0c1734)] p-2">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="rounded-md border border-white/10 bg-white/5" />
            ))}
          </div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
        <div className="glass-card rounded-2xl border-white/15 p-3">
          <div className="mx-auto flex h-44 w-24 items-center justify-center rounded-[1.8rem] border border-white/20 bg-[#070c18]">
            <div className="h-36 w-16 rounded-xl border border-primary/30 bg-[linear-gradient(160deg,#101a33,#060a15)]" />
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            <Smartphone className="me-1 inline h-3.5 w-3.5" /> {dictionary.twinTv.deviceLabel.android}
          </p>
        </div>
        <div className="glass-card rounded-2xl border-white/15 p-3">
          <div className="mx-auto h-16 w-28 rounded-full border border-white/20 bg-[#090f1f] px-4 py-2">
            <div className="grid h-full grid-cols-3 gap-1">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="rounded-full bg-white/10" />
              ))}
            </div>
          </div>
          <p className="mt-3 text-center text-xs text-muted-foreground">{dictionary.twinTv.deviceLabel.remote}</p>
        </div>
      </div>
      <span className="absolute -top-2 start-4 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/20 px-3 py-1 text-xs text-primary-foreground">
        <PlayCircle className="h-3.5 w-3.5" />
        {dictionary.hero.twinTvCardSubtitle}
      </span>
    </div>
  );
};

const NocCharts = ({ dictionary }) => {
  const chartTitles = dictionary.smartNoc.chartTitles;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="glass-card border-white/15">
        <CardHeader>
          <CardTitle className="text-base">{chartTitles.bandwidthUsage}</CardTitle>
        </CardHeader>
        <CardContent className="h-52 min-h-[208px]">
          <div className="flex h-full items-end gap-2 rounded-xl border border-white/10 bg-[#0d1428] p-4">
            {siteConfig.smartNoc.charts.bandwidthUsage.map((point) => (
              <div key={point.name} className="flex flex-1 flex-col items-center gap-2">
                <div
                  className="w-full rounded-t-md bg-gradient-to-t from-primary/40 to-primary"
                  style={{ height: `${Math.max(point.value, 10)}%` }}
                />
                <span className="font-mono-tech text-[10px] text-muted-foreground">{point.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card border-white/15">
        <CardHeader>
          <CardTitle className="text-base">{chartTitles.networkTraffic}</CardTitle>
        </CardHeader>
        <CardContent className="h-52 min-h-[208px] space-y-3">
          {siteConfig.smartNoc.charts.networkTraffic.map((row) => (
            <div key={row.name} className="rounded-xl border border-white/10 bg-[#0d1428] p-3">
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{row.name}</span>
                <span className="font-mono-tech">U:{row.upload}% / D:{row.download}%</span>
              </div>
              <div className="space-y-2">
                <div className="h-2 rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-cyan-300" style={{ width: `${row.upload}%` }} />
                </div>
                <div className="h-2 rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${row.download}%` }} />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="glass-card border-white/15">
        <CardHeader>
          <CardTitle className="text-base">{chartTitles.alerts}</CardTitle>
        </CardHeader>
        <CardContent className="h-52 min-h-[208px]">
          <div className="grid h-full gap-3 rounded-xl border border-white/10 bg-[#0d1428] p-4">
            {siteConfig.smartNoc.charts.alerts.map((alert) => (
              <div key={alert.name} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#111a31] px-3 py-2">
                <span className="text-sm">{alert.name}</span>
                <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-primary/25 px-2 font-mono-tech text-xs text-primary">
                  {alert.value}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card border-white/15">
        <CardHeader>
          <CardTitle className="text-base">{chartTitles.topLocations}</CardTitle>
        </CardHeader>
        <CardContent className="h-52 min-h-[208px] space-y-3">
          {siteConfig.smartNoc.charts.topLocations.map((location) => (
            <div key={location.name}>
              <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                <span>{location.name}</span>
                <span className="font-mono-tech">{location.value}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/10">
                <div className="h-full rounded-full bg-[#2ef2a5]" style={{ width: `${location.value}%` }} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export const LandingPage = () => {
  const [lang, setLang] = useState("ar");
  const [menuOpen, setMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [coveragePreview, setCoveragePreview] = useState([]);
  const [formState, setFormState] = useState({
    name: "",
    phone: "",
    service_type: "",
    message: "",
  });

  const dictionary = translations[lang];
  const direction = getDirection(lang);
  const isArabic = lang === "ar";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = direction;
    document.body.dir = direction;

    document.title = dictionary.seo.title;

    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", dictionary.seo.description);
  }, [dictionary, direction, lang]);

  useEffect(() => {
    const loadCoveragePreview = async () => {
      try {
        const data = await fetchCoverageLocations({ status: "all", lang });
        setCoveragePreview((data.items || []).slice(0, 4));
      } catch (error) {
        // silent on landing
      }
    };

    loadCoveragePreview();
  }, [lang]);

  const handleChange = (field) => (event) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const hasInvalidField =
      formState.name.trim().length < 2 ||
      formState.phone.trim().length < 7 ||
      formState.service_type.trim().length < 2 ||
      formState.message.trim().length < 10;

    if (hasInvalidField) {
      toast.error(dictionary.contact.validation);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact/submit`, {
        ...formState,
        language: lang,
      });

      toast.success(response.data?.message || dictionary.contact.success);
      setFormState({ name: "", phone: "", service_type: "", message: "" });
    } catch (error) {
      const status = error?.response?.status;
      const serverError =
        error?.response?.data?.detail || error?.response?.data?.message || dictionary.contact.error;

      if (status === 429) {
        toast.error(dictionary.contact.rateLimit);
      } else {
        toast.error(serverError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappHref = `https://wa.me/${siteConfig.contact.whatsapp.replace(/\D/g, "")}`;
  const callHref = `tel:${siteConfig.contact.phone.replace(/\D/g, "")}`;

  return (
    <div className={`app-shell ${isArabic ? "font-ar" : "font-en"}`} dir={direction}>
      <header className="coverage-topbar sticky top-0 z-50">
        <div className="container-shell flex min-h-[72px] items-center justify-between gap-4 py-2">
          <a href="#home" className="inline-flex items-center" aria-label={siteConfig.brand.name}>
            <img src={twinLogo} alt="TWIN TC logo" className="brand-logo logo-glow h-12 w-auto object-contain sm:h-14 lg:h-16" />
          </a>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="Main navigation">
            {navTargets.map((item) => (
              <a
                key={item.key}
                href={item.href}
                data-testid={item.testId}
                className="relative text-sm text-foreground/85 hover:text-foreground"
              >
                {dictionary.nav[item.key]}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <a href={whatsappHref} target="_blank" rel="noreferrer" className="header-contact-btn" data-testid={LANDING.headerWhatsappButton}>
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a href={callHref} className="header-contact-btn" data-testid={LANDING.headerCallButton}>
              <Phone className="h-4 w-4" /> {isArabic ? "اتصال" : "Call"}
            </a>
            <div className="flex items-center rounded-xl border border-white/15 bg-card/70 p-1">
              <button
                type="button"
                data-testid={LANDING.languageSwitchAr}
                onClick={() => setLang("ar")}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                  lang === "ar" ? "bg-primary text-primary-foreground" : "text-foreground/80"
                }`}
              >
                {dictionary.common.languageAr}
              </button>
              <button
                type="button"
                data-testid={LANDING.languageSwitchEn}
                onClick={() => setLang("en")}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                  lang === "en" ? "bg-primary text-primary-foreground" : "text-foreground/80"
                }`}
              >
                {dictionary.common.languageEn}
              </button>
            </div>

            <Button asChild className="h-10 rounded-xl bg-primary px-5 text-primary-foreground hover:bg-primary/90" data-testid={LANDING.clientPortalButton}>
              <a href={siteConfig.cta.clientPortal}>{dictionary.common.clientPortal}</a>
            </Button>
          </div>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 border-white/20 bg-card/50 lg:hidden"
                data-testid={LANDING.mobileMenuButton}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={isArabic ? "left" : "right"} className="mobile-sheet-glass w-[88%] border-white/10 text-foreground sm:max-w-sm">
              <SheetHeader className="sr-only">
                <SheetTitle>{dictionary.common.mainMenu}</SheetTitle>
                <SheetDescription>{dictionary.contact.subtitle}</SheetDescription>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-4">
                {navTargets.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    className="rounded-xl border border-white/10 px-4 py-3 text-sm"
                    onClick={() => setMenuOpen(false)}
                  >
                    {dictionary.nav[item.key]}
                  </a>
                ))}
                <a href="#support" className="rounded-xl border border-white/10 px-4 py-3 text-sm" onClick={() => setMenuOpen(false)}>
                  {dictionary.common.contactUs}
                </a>
                <a href={whatsappHref} target="_blank" rel="noreferrer" className="rounded-xl border border-[#2ef2a5]/35 bg-[#2ef2a5]/20 px-4 py-3 text-sm">
                  WhatsApp
                </a>
                <a href={callHref} className="rounded-xl border border-white/10 px-4 py-3 text-sm">
                  {dictionary.common.callNow}
                </a>
                <div className="mt-2 flex items-center justify-between rounded-xl border border-white/10 p-2">
                  <button type="button" onClick={() => setLang("ar")} className="rounded-lg px-3 py-2 text-sm">
                    AR
                  </button>
                  <button type="button" onClick={() => setLang("en")} className="rounded-lg px-3 py-2 text-sm">
                    EN
                  </button>
                </div>
                <Button asChild className="rounded-xl bg-primary text-primary-foreground">
                  <a href={siteConfig.cta.clientPortal}>{dictionary.common.clientPortal}</a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main>
        <section id="home" className="relative overflow-hidden pb-20 pt-16 sm:pt-20">
          <NetworkBackdrop />
          <div className="container-shell relative z-10">
            <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-6">
                <span className="inline-flex rounded-full border border-primary/30 bg-primary/15 px-4 py-1.5 text-xs text-primary">
                  {dictionary.common.techBadge}
                </span>
                <h1 className="max-w-2xl text-4xl font-bold leading-[1.35] sm:text-5xl lg:text-6xl">{dictionary.hero.headline}</h1>
                <p className="max-w-2xl text-sm leading-8 text-muted-foreground sm:text-base">{dictionary.hero.subtitle}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild className="h-11 rounded-xl bg-primary px-6 text-primary-foreground hover:bg-primary/90" data-testid={LANDING.heroCoverageCta}>
                    <a href={siteConfig.cta.coverageAnchor}>{dictionary.hero.coverageCta}</a>
                  </Button>
                  <Button asChild variant="outline" className="h-11 rounded-xl border-white/20 bg-card/90 px-6" data-testid={LANDING.heroExploreCta}>
                    <a href={siteConfig.cta.servicesAnchor}>{dictionary.hero.exploreCta}</a>
                  </Button>
                  <a href={whatsappHref} target="_blank" rel="noreferrer" className="header-contact-btn" data-testid={LANDING.heroWhatsAppButton}>
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                  <a href={callHref} className="header-contact-btn" data-testid={LANDING.heroCallButton}>
                    <Phone className="h-4 w-4" /> {dictionary.common.callNow}
                  </a>
                </div>
                <Card className="glass-card max-w-xs border-primary/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{dictionary.hero.twinTvCardTitle}</CardTitle>
                    <CardDescription>{dictionary.hero.twinTvCardSubtitle}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild className="h-10 w-full rounded-xl bg-primary text-primary-foreground" data-testid={LANDING.heroTwinTvDownloadCta}>
                      <a href={siteConfig.cta.twinTvAndroid} target="_blank" rel="noreferrer">
                        {dictionary.common.downloadApp}
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
              <div className="relative flex justify-center py-2 lg:py-4">
                <img
                  src={twinLogo}
                  alt="TWIN TC official logo"
                  className="brand-logo logo-glow mx-auto h-[190px] w-auto sm:h-[230px] lg:h-[320px] xl:h-[360px]"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="pb-8">
          <div className="container-shell">
            <SectionHeader title={dictionary.stats.title} />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {siteConfig.stats.map((item) => (
                <Card key={item.key} className="glass-card border-white/15" data-testid={LANDING.statCard}>
                  <CardContent className="p-5">
                    <p className="font-mono-tech text-2xl font-semibold text-gradient">{item.value}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{dictionary.stats.labels[item.key]}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="services" className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.internetServices.title} subtitle={dictionary.internetServices.subtitle} />
            <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
              {dictionary.internetServices.items.map((item) => {
                const Icon = serviceIcons[item.key] || Zap;
                const isCoverageCard = item.key === "coverage";

                return (
                  <Card
                    key={item.key}
                    id={isCoverageCard ? "coverage" : undefined}
                    className="glass-card border-white/12 hover:border-primary/40"
                    data-testid={LANDING.serviceCard}
                  >
                    <CardContent className="space-y-4 p-6">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/20 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="text-lg font-semibold">{item.title}</h3>
                      <p className="text-sm leading-7 text-muted-foreground">{item.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section id="coverage" className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.coverageMap.title} subtitle={dictionary.coverageMap.subtitle} />
            <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
              <Card className="glass-card border-white/12">
                <CardContent className="grid gap-3 p-5 sm:grid-cols-2">
                  {coveragePreview.map((location) => {
                    const statusMeta = coverageUtils.getCoverageStatusMeta(location.service_status, lang);
                    const name = isArabic ? location.region_name_ar : location.region_name_en;
                    return (
                      <div key={location.id} className="rounded-xl border border-white/10 bg-[#0e152a] p-4" data-testid={LANDING.coveragePreviewCard}>
                        <p className="mb-2 text-sm font-semibold">{name}</p>
                        <Badge style={{ backgroundColor: `${statusMeta.color}25`, color: statusMeta.color, borderColor: `${statusMeta.color}66` }}>
                          {statusMeta.label}
                        </Badge>
                        <p className="mt-2 text-xs text-muted-foreground">{location.city}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              <Card className="glass-card border-white/12">
                <CardHeader>
                  <CardTitle>{dictionary.coverageMap.openMap}</CardTitle>
                  <CardDescription>{dictionary.coverageMap.previewNote}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button asChild className="h-11 w-full rounded-xl bg-primary text-primary-foreground" data-testid={LANDING.openCoverageMapButton}>
                    <Link to={siteConfig.cta.openCoverageMap}>{dictionary.coverageMap.openMap}</Link>
                  </Button>
                  <Button asChild variant="outline" className="h-11 w-full rounded-xl border-white/20 bg-card/80">
                    <Link to={siteConfig.cta.adminCoverage}>{dictionary.coverageMap.adminLink}</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section id="twin-tv" className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.twinTv.title} subtitle={dictionary.twinTv.subtitle} />
            <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  {dictionary.twinTv.features.map((feature) => (
                    <Card key={feature} className="glass-card border-white/10">
                      <CardContent className="flex items-center gap-3 p-4">
                        <ShieldCheck className="h-4 w-4 text-[#2ef2a5]" />
                        <span className="text-sm">{feature}</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="h-11 rounded-xl bg-primary px-6 text-primary-foreground" data-testid={LANDING.twinTvDownloadButton}>
                    <a href={siteConfig.cta.twinTvAndroid} target="_blank" rel="noreferrer">
                      {dictionary.common.downloadApp}
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="h-11 rounded-xl border-white/20 bg-card/90 px-6" data-testid={LANDING.twinTvLearnMoreButton}>
                    <a href="#about">{dictionary.common.learnMore}</a>
                  </Button>
                  <Button type="button" variant="secondary" className="h-11 rounded-xl bg-secondary text-secondary-foreground" disabled>
                    {dictionary.common.iosLabel}: {dictionary.common.comingSoon}
                  </Button>
                </div>
              </div>
              <DeviceMockups dictionary={dictionary} />
            </div>
          </div>
        </section>

        <section id="smart-noc" className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.smartNoc.title} subtitle={dictionary.smartNoc.subtitle} />
            <Card className="glass-card mb-6 border-white/15">
              <CardContent className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
                {siteConfig.smartNoc.cards.map((item) => (
                  <div key={item} className="rounded-xl border border-white/10 bg-[#0e152a] px-4 py-3">
                    <div className="mb-1 inline-flex items-center gap-2 text-[#2ef2a5]">
                      <Activity className="h-3.5 w-3.5" />
                      <span className="text-xs font-semibold">{dictionary.smartNoc.online}</span>
                    </div>
                    <p className="text-sm">{item}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
            <NocCharts dictionary={dictionary} />
            <div className="mt-6">
              <Button type="button" className="h-11 rounded-xl bg-primary px-6 text-primary-foreground" data-testid={LANDING.nocDashboardButton}>
                {dictionary.smartNoc.button}
              </Button>
            </div>
          </div>
        </section>

        <section id="cloud" className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.cloudSolutions.title} subtitle={dictionary.cloudSolutions.subtitle} />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {dictionary.cloudSolutions.items.map((item, idx) => {
                const Icon = cloudIcons[idx % cloudIcons.length];
                return (
                  <Card key={item} className="glass-card border-white/12 hover:border-primary/40">
                    <CardContent className="space-y-4 p-5">
                      <Icon className="h-5 w-5 text-primary" />
                      <p className="text-sm font-medium">{item}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.networkStatus.title} subtitle={dictionary.networkStatus.subtitle} />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {siteConfig.networkStatus.map((item) => (
                <StatusRing
                  key={item.key}
                  label={dictionary.networkStatus.labels[item.key]}
                  value={item.value}
                  testId={LANDING.networkStatusItem}
                />
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              {dictionary.networkStatus.lastUpdated}: {new Date().toLocaleDateString(isArabic ? "ar" : "en-US")}
            </p>
          </div>
        </section>

        <section id="about" className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.about.title} />
            <Card className="glass-card border-white/12">
              <CardContent className="p-7">
                <p className="text-sm leading-8 text-foreground/90 sm:text-base">{dictionary.about.body}</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="support" className="py-16">
          <div className="container-shell">
            <SectionHeader title={dictionary.contact.title} subtitle={dictionary.contact.subtitle} />
            <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
              <Card className="glass-card border-white/12">
                <CardContent className="space-y-4 p-6">
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={LANDING.contactWhatsappButton}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#2ef2a5]/35 bg-[#2ef2a5]/20 text-sm font-semibold text-white hover:bg-[#2ef2a5]/28"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {dictionary.contact.whatsapp}
                  </a>

                  <a href={callHref} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-card/80 text-sm">
                    <Phone className="h-4 w-4" /> {dictionary.common.callNow}
                  </a>

                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-primary" /> {siteConfig.contact.phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-primary" /> {siteConfig.contact.supportPhone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" /> {siteConfig.contact.email}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" /> {siteConfig.contact.location}
                    </p>
                  </div>

                  <Button asChild variant="outline" className="h-11 w-full rounded-xl border-white/20 bg-card/90" data-testid={LANDING.contactSupportButton}>
                    <a href={`mailto:${siteConfig.contact.email}`}>{dictionary.contact.support}</a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/12">
                <CardContent className="p-6">
                  <form className="space-y-4" onSubmit={handleSubmit} data-testid={LANDING.contactForm}>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm text-foreground/90">
                          {dictionary.contact.name}
                        </label>
                        <Input
                          id="name"
                          name="name"
                          value={formState.name}
                          onChange={handleChange("name")}
                          placeholder={dictionary.contact.name}
                          data-testid={LANDING.contactNameInput}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm text-foreground/90">
                          {dictionary.contact.phone}
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formState.phone}
                          onChange={handleChange("phone")}
                          placeholder={dictionary.contact.phone}
                          data-testid={LANDING.contactPhoneInput}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm text-foreground/90">{dictionary.contact.serviceType}</label>
                      <Select
                        value={formState.service_type}
                        onValueChange={(value) => setFormState((prev) => ({ ...prev, service_type: value }))}
                      >
                        <SelectTrigger data-testid={LANDING.contactServiceSelect}>
                          <SelectValue placeholder={dictionary.contact.serviceType} />
                        </SelectTrigger>
                        <SelectContent>
                          {dictionary.contact.serviceOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm text-foreground/90">
                        {dictionary.contact.message}
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formState.message}
                        onChange={handleChange("message")}
                        placeholder={dictionary.contact.message}
                        data-testid={LANDING.contactMessageTextarea}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      className="h-11 w-full rounded-xl bg-primary text-primary-foreground"
                      disabled={submitting}
                      data-testid={LANDING.contactSubmitButton}
                    >
                      {submitting ? dictionary.contact.sending : dictionary.contact.send}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-12">
        <div className="container-shell grid gap-8 lg:grid-cols-[1.2fr_1fr_1fr_1fr]">
          <div className="space-y-4">
            <img src={twinLogo} alt="TWIN TC logo footer" className="brand-logo logo-glow h-12 w-auto sm:h-14" />
            <p className="text-sm leading-7 text-muted-foreground">{dictionary.footer.tagline}</p>
            <div className="flex gap-2">
              {socialPlatforms.map(({ key, icon: Icon }) => (
                <a
                  key={key}
                  href={siteConfig.social[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={key}
                  data-testid={LANDING.footerSocialLink}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 bg-card/90 hover:border-primary/40"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-foreground">{dictionary.footer.quickLinks}</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {navTargets.map((item) => (
                <li key={item.key}>
                  <a href={item.href} className="hover:text-foreground">
                    {dictionary.nav[item.key]}
                  </a>
                </li>
              ))}
              <li>
                <Link to={siteConfig.cta.openCoverageMap} className="hover:text-foreground">
                  {dictionary.coverageMap.openMap}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-foreground">{dictionary.footer.services}</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {dictionary.cloudSolutions.items.slice(0, 5).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-foreground">{dictionary.footer.support}</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>{siteConfig.contact.phone}</li>
              <li>{siteConfig.contact.supportPhone}</li>
              <li>{siteConfig.contact.email}</li>
              <li>{siteConfig.contact.location}</li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={whatsappHref} target="_blank" rel="noreferrer" className="header-contact-btn">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <a href={callHref} className="header-contact-btn">
                <Phone className="h-4 w-4" /> {dictionary.common.callNow}
              </a>
              <a href="#support" className="header-contact-btn">
                <Mail className="h-4 w-4" /> {dictionary.common.contactUs}
              </a>
            </div>
          </div>
        </div>
        <div className="container-shell mt-10 border-t border-white/10 pt-5 text-center text-xs text-muted-foreground">
          {siteConfig.brand.copyright}
        </div>
      </footer>
    </div>
  );
};
